import { OnboardingStorage, OnboardingProgress } from './onboarding-types';

/**
 * LocalStorage implementation of OnboardingStorage
 * Stores progress in browser localStorage with key format: onboarding:{userId}:{flowId}
 */
export class LocalStorageOnboardingStorage implements OnboardingStorage {
  /**
   * Generates the localStorage key for a user/flow combination
   */
  private getKey(userId: string, flowId: string): string {
    return `onboarding:${userId}:${flowId}`;
  }

  /**
   * Retrieves progress for a user's flow
   * Returns null if no progress exists
   */
  async getProgress(
    userId: string,
    flowId: string,
  ): Promise<OnboardingProgress | null> {
    const key = this.getKey(userId, flowId);
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  }

  /**
   * Marks a single step as complete
   * Creates new progress entry if none exists
   */
  async markStepComplete(
    userId: string,
    flowId: string,
    stepId: string,
  ): Promise<void> {
    const progress =
      (await this.getProgress(userId, flowId)) ||
      ({
        flowId,
        completedSteps: [],
        status: 'incomplete',
        lastUpdated: new Date().toISOString(),
      } as OnboardingProgress);

    if (!progress.completedSteps.includes(stepId)) {
      progress.completedSteps.push(stepId);
    }
    progress.lastUpdated = new Date().toISOString();

    localStorage.setItem(this.getKey(userId, flowId), JSON.stringify(progress));
  }

  /**
   * Marks the entire flow as skipped (user dismissed)
   */
  async markFlowSkipped(userId: string, flowId: string): Promise<void> {
    const progress =
      (await this.getProgress(userId, flowId)) ||
      ({
        flowId,
        completedSteps: [],
        status: 'skipped',
        lastUpdated: new Date().toISOString(),
      } as OnboardingProgress);

    progress.status = 'skipped';
    progress.lastUpdated = new Date().toISOString();
    localStorage.setItem(this.getKey(userId, flowId), JSON.stringify(progress));
  }

  /**
   * Marks the entire flow as completed
   */
  async markFlowCompleted(userId: string, flowId: string): Promise<void> {
    const progress = await this.getProgress(userId, flowId);
    if (progress) {
      progress.status = 'completed';
      progress.lastUpdated = new Date().toISOString();
      localStorage.setItem(
        this.getKey(userId, flowId),
        JSON.stringify(progress),
      );
    }
  }

  /**
   * Completely resets progress for a flow (used for restart)
   */
  async resetProgress(userId: string, flowId: string): Promise<void> {
    localStorage.removeItem(this.getKey(userId, flowId));
  }
}
